/**
 * PetaCMS admin panel entry point (isolated superadmin SPA).
 * Hosted on admin.peta-cms.com, behind Cloudflare Access.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { initPostHog, trackPageView } from './lib/posthog.js';
import { initSentry } from './lib/sentry.js';
import { router } from './router.js';
import './styles/globals.css';

initSentry();
initPostHog();

router.subscribe('onResolved', ({ toLocation }) => {
  trackPageView(toLocation.pathname);
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof Error && 'status' in error) {
          const status = (error as unknown as { status: number }).status;
          if (status >= 400 && status < 500) return false;
        }
        return failureCount < 3;
      },
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
